<template>
  <VitalSignsPreviewModal
    v-if="activeCase"
    v-model:show="showVitalSignsPreviewModal"
    :caze="activeCase.case"
    :vital-signs="activeCase.vitalSigns"
    :laboratory="activeCase.laboratory"
    :organ-support="activeCase.organSupport"
    :eds-score="activeCase.edsScore"
    @update:show="
      (val) => {
        showVitalSignsPreviewModal = val
        if (!val) activeCase = null
      }
    "
  />

  <VCard
    :data-testid="`consultation-card-${consultation.id}`"
    @click="openVitalSignsModal"
    class="cursor-pointer"
  >
    <div class="pt-6 pb-5">
      <div class="px-4 sm:!px-12 gap-4 flex flex-column">
        <div class="flex gap-1">
          <DirectConsultationTypeChip
            :variant="'outlined'"
            :consultation-type="consultation.urgency"
            class="text-sm font-semibold leading-[20]"
            data-testid="consultation-type-chip"
            dark
          />

          <ConsultationTimer
            v-if="
              isUrgencyWithCountdown(consultation.urgency) &&
              !!consultation.nextEscalationAt &&
              consultation.status === 'created'
            "
            :urgency="consultation.urgency"
            :start-at="consultation.assignedAt"
            :show-icon="false"
            :prefix="t('Waiting for')"
            :timer-mode="'up'"
            data-testid="consultation-countdown"
            dark
          />
        </div>

        <div class="flex gap-4">
          <PersonAvatar
            :first-name="consultation.case.patient.firstName ?? ''"
            :last-name="consultation.case.patient.lastName ?? ''"
            :type="'patient'"
          />

          <div class="text-left flex-1 min-w-0">
            <VCardTitle class="text-base font-semibold pa-0">
              <p data-testid="patient-title">{{ consultationDetails.patientName }}</p>
            </VCardTitle>

            <VCardSubtitle
              class="pa-0 !text-base text-subtitle self-center whitespace-normal"
              data-testid="patient-details"
            >
              <span>{{ consultationDetails.age }}</span>
              <span class="mx-1">·</span>
              <span>{{ consultationDetails.caseId }}</span>
              <span class="mx-1">·</span>
              <span>{{ consultation.case.ward.name }}</span>
              <span class="mx-1">·</span>
              <span>{{ consultation.case.department.name }}</span>
              <span class="mx-1">·</span>
              <span>{{ consultation.case.hospital.name }}</span>
            </VCardSubtitle>
          </div>
        </div>
      </div>
    </div>

    <DividerLine />
    <VProgressLinear v-if="caseLoading" indeterminate color="primary" />

    <VCardItem class="px-6 pb-0">
      <ConsultationStepper :consultation-step="consultation.status" />

      <NoRecipientsBanner v-if="showNoRecipientsWarning" class="mx-6 mb-4" />

      <div class="px-6 pb-4 flex flex-column gap-2">
        <div>
          <div
            v-if="!isEditing"
            class="flex-col flex items-start gap-4 lg:flex-row lg:items-center lg:gap-16"
          >
            <div data-testid="medical-field" class="text-sm flex items-center gap-2">
              <p class="font-semibold">{{ t('Request to Department') }}:</p>

              <div>
                <p>{{ translateMedicalFieldsDTO(consultation.medicalField, t) }}</p>

                <span data-testid="consultation-time" class="text-grey-dark text-xs"
                  >{{
                    t('consultationTime', {
                      date: consultation.assignedTo
                        ? formatDateShort(consultation.assignedAt)
                        : formatDateShort(consultation.createdAt),
                      hour: consultation.assignedTo
                        ? formatTime(consultation.assignedAt)
                        : formatTime(consultation.createdAt)
                    })
                  }}
                </span>
              </div>
            </div>

            <div
              v-if="consultation.relationToUser === ConsultationRequestUserRelationDTO.OUTGOING"
              data-testid="assigned-to"
            >
              <div class="text-sm flex items-center gap-2">
                <p class="font-semibold">{{ t('Assigned to') }}:</p>

                <p v-if="!consultation.assignedTo" data-testid="not-assigned-label">
                  {{ t('Not assigned yet') }}
                </p>

                <div v-else class="flex gap-2 items-center" data-testid="assigned-user-data">
                  <PersonAvatar
                    :first-name="consultation.assignedTo.user?.firstName ?? ''"
                    :last-name="consultation.assignedTo.user?.lastName ?? ''"
                    :type="'physician'"
                    :size="32"
                  />

                  <div>
                    <p data-testid="receiver-details" class="font-semibold">
                      {{ consultation.assignedTo.user?.title ?? '' }}
                      {{ consultation.assignedTo.user?.firstName ?? '' }}
                      {{ consultation.assignedTo.user?.lastName ?? '' }}
                    </p>

                    <span
                      v-if="!consultation.assignedTo.departments.length"
                      class="text-xs text-subtitle"
                      data-testid="department"
                      >{{ t('No department assigned') }}
                    </span>

                    <span
                      v-else-if="consultation.assignedTo.departments.length === 1"
                      class="text-xs text-subtitle"
                      data-testid="department"
                      >{{ consultation.assignedTo.departments[0].name }}
                    </span>

                    <MoreItemsLabel
                      v-else
                      :items="
                        convertUserDetailsIntoSimpleObjects(consultation.assignedTo.departments)
                      "
                      :main-item-id="consultation.case.department.id"
                      class="text-sm"
                    />

                    <span class="mx-1">·</span>

                    <span
                      v-if="!consultation.assignedTo.hospitals.length"
                      class="text-xs text-subtitle"
                      data-testid="hospital"
                      >{{ t('No hospital assigned') }}</span
                    >

                    <span
                      v-else-if="consultation.assignedTo.hospitals.length === 1"
                      class="text-xs text-subtitle"
                      data-testid="hospital"
                      >{{ consultation.assignedTo.hospitals[0].name }}</span
                    >

                    <MoreItemsLabel
                      v-else
                      :items="
                        convertUserDetailsIntoSimpleObjects(consultation.assignedTo.hospitals)
                      "
                      :main-item-id="consultation.case.hospital.id"
                      class="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="
                consultation.createdBy.email !== user?.email &&
                (consultation.status !== ConsultationRequestStatusDTO.CREATED ||
                  consultation.relationToUser === 'outgoing')
              "
              data-testid="created-by"
            >
              <div class="text-sm flex items-center gap-2">
                <p class="font-semibold">{{ t('Created by') }}:</p>

                <div class="flex gap-2 items-center">
                  <PersonAvatar
                    :first-name="consultation.createdBy.user.firstName ?? ''"
                    :last-name="consultation.createdBy.user.lastName ?? ''"
                    :type="'physician'"
                    :size="32"
                  />

                  <div>
                    <p data-testid="creator-details" class="font-semibold">
                      {{ consultation.createdBy.user.title ?? '' }}
                      {{ consultation.createdBy.user.firstName ?? '' }}
                      {{ consultation.createdBy.user.lastName ?? '' }}
                    </p>

                    <span
                      v-if="!consultation.createdBy.departments.length"
                      class="text-xs text-subtitle"
                      data-testid="department"
                      >{{ t('No department assigned') }}
                    </span>

                    <span
                      v-else-if="consultation.createdBy.departments.length === 1"
                      class="text-xs text-subtitle"
                      data-testid="department"
                      >{{ consultation.createdBy.departments[0].name }}
                    </span>

                    <MoreItemsLabel
                      v-else
                      :items="
                        convertUserDetailsIntoSimpleObjects(consultation.createdBy.departments)
                      "
                      :main-item-id="consultation.case.department.id"
                      class="text-sm"
                    />

                    <span class="mx-1">·</span>

                    <span
                      v-if="!consultation.createdBy.hospitals.length"
                      class="text-xs text-subtitle"
                      data-testid="hospital"
                      >{{ t('No hospital assigned') }}</span
                    >

                    <span
                      v-else-if="consultation.createdBy.hospitals.length === 1"
                      class="text-xs text-subtitle"
                      data-testid="hospital"
                      >{{ consultation.createdBy.hospitals[0].name }}</span
                    >

                    <MoreItemsLabel
                      v-else
                      :items="convertUserDetailsIntoSimpleObjects(consultation.createdBy.hospitals)"
                      :main-item-id="consultation.case.hospital.id"
                      class="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            <VBtn
              v-if="showEditButton && hasChiefPhysicianShift"
              icon
              density="compact"
              size="small"
              variant="plain"
              class="min-h-0 p-0 text-body-2"
              @click.prevent="startEdit"
              data-testid="edit-button"
            >
              <VIcon :icon="consultation.assignedTo ? 'edit' : 'add'" size="16" />
            </VBtn>
          </div>

          <div v-else class="flex items-center gap-2 flex-wrap">
            <VAutocomplete
              @mousedown.stop
              @touchstart.stop
              v-model="selectedDoctor"
              :items="filteredDoctors"
              item-title="text"
              item-value="value"
              :label="t('Select a doctor')"
              density="compact"
              :loading="isLoadingDoctors"
              data-testid="doctor-select"
              class="text-body-2 w-full max-w-[200px] sm:min-w-[300px] sm:w-auto"
              :menu-props="{ contentClass: 'text-body-2' }"
              :clearable="!!(selectedDoctor || search)"
              hide-details
              :no-data-text="t('No matching doctors')"
            />
            <VBtn
              v-if="canSave"
              icon
              density="compact"
              size="small"
              variant="plain"
              class="min-h-0 p-0 text-body-2"
              @click.stop="assignDoctor(consultation.id)"
              :loading="isSavingDoctor"
              data-testid="save-button"
            >
              <VIcon icon="check" size="16" />
            </VBtn>
            <VBtn
              icon
              density="compact"
              size="small"
              variant="plain"
              class="min-h-0 p-0 text-body-2"
              @click.stop="cancelEdit"
              data-testid="cancel-button"
            >
              <VIcon icon="cancel" size="16" />
            </VBtn>
          </div>
        </div>
      </div>

      <DividerLine
        v-if="question || consultationUrgencyWithDetails.includes(consultation.urgency)"
      />

      <div class="px-0 sm:!px-6 py-2 flex flex-column gap-2">
        <div
          v-if="question && consultation.relationToUser === 'incoming'"
          class="flex flex-col gap-2"
          data-testid="outside-question"
        >
          <div class="text-subtitle-2">{{ t('Question') }}:</div>
          <div data-testid="question" class="text-body-2 flex-1 max-h-[200px] overflow-auto">
            <FormattedMedicalText :text="question" />
          </div>
        </div>

        <VExpansionPanels v-if="consultationUrgencyWithDetails.includes(consultation.urgency)">
          <VExpansionPanel
            class="consultation-details-expansion-panel"
            elevation="0"
            expand-icon="chevron_right"
            @mousedown.stop
            @click.stop="showDetails = !showDetails"
          >
            <template #title>
              <p class="text-consultation-card-dark font-semibold">
                {{ showDetails ? t('Hide consultation details') : t('Show consultation details') }}
              </p>
            </template>

            <template #text>
              <div
                v-if="question && consultation.relationToUser === 'outgoing'"
                class="flex flex-col gap-2"
                data-testid="inside-question"
              >
                <div class="text-subtitle-2">{{ t('Question') }}:</div>
                <div class="text-body-2 flex-1 max-h-[200px] overflow-auto">
                  <FormattedMedicalText :text="question" />
                </div>
              </div>

              <div class="text-sm flex flex-column gap-2 max-h-[300px] overflow-auto hover:bg-grey">
                <div v-if="medicalHistory" class="flex flex-col gap-2" data-testid="medial-history">
                  <div class="text-subtitle-2">{{ t('Medical History') }}:</div>
                  <div class="text-body-2 flex-1">
                    <FormattedMedicalText :text="medicalHistory" />
                  </div>
                </div>

                <div v-if="diagnosis" class="flex flex-col gap-2" data-testid="diagnosis">
                  <div class="text-subtitle-2">{{ t('Diagnosis') }}:</div>
                  <div class="text-body-2 flex-1">
                    <FormattedMedicalText :text="diagnosis" />
                  </div>
                </div>

                <div v-if="therapy" class="flex flex-col gap-2" data-testid="therapy">
                  <div class="text-subtitle-2">{{ t('Therapy') }}:</div>
                  <div class="text-body-2 flex-1">
                    <FormattedMedicalText :text="therapy" />
                  </div>
                </div>

                <div
                  v-for="field in consultation.customFields"
                  :key="field.id"
                  class="flex flex-col gap-2"
                  data-testid="custom-field"
                >
                  <div class="text-subtitle-2">{{ field.label }}:</div>
                  <div class="text-body-2 flex-1 max-h-[200px] overflow-auto">
                    <FormattedMedicalText :text="field.value" />
                  </div>
                </div>
              </div>
            </template>
          </VExpansionPanel>
        </VExpansionPanels>
      </div>
    </VCardItem>

    <div
      v-if="consultation.status !== 'completed'"
      class="pl-12 pr-6 pb-6"
      data-testid="card-footer"
    >
      <DividerLine class="w-100" />
      <VCardActions class="gap-5 sm:gap-0 pt-5">
        <div v-if="consultation.status === 'created'">
          <p
            v-if="consultation.relationToUser === 'outgoing'"
            class="block sm:inline-flex sm:items-center"
          >
            <span class="text-sm text-subtitle font-semibold" data-testid="escalation-counter">
              {{
                t('Escalation level') +
                ': ' +
                t('escalationLevelStep', {
                  current: consultation.escalationProgress?.currentStepNumber ?? 0,
                  total: consultation.escalationProgress?.totalSteps ?? 0
                })
              }}
            </span>

            <span
              v-if="
                !showNoRecipientsWarning &&
                consultation.escalationProgress?.currentStepNumber !==
                  consultation.escalationProgress?.totalSteps
              "
              class="sm:inline-flex"
            >
              <span class="mx-1">·</span>
              <TimeCounter
                :show-icon="false"
                :end-at="consultation.nextEscalationAt"
                :prefix="t('Next escalation level in')"
                class="text-subtitle text-sm font-semibold"
                timer-mode="down"
                data-testid="escalation-timer"
              />
            </span>
          </p>

          <TimeCounter
            v-else
            class="text-subtitle text-sm font-semibold"
            timer-mode="down"
            :show-icon="false"
            :end-at="consultation.nextEscalationAt"
            :prefix="locale === 'de' ? t('Still') : undefined"
            :suffix="t('left to join')"
            data-testid="join-consultation-timer"
          />
        </div>

        <ConsultationCardActions
          v-if="showActionButtons"
          data-testid="action-buttons"
          :consultation="consultation"
        />
      </VCardActions>
    </div>
  </VCard>
</template>

<script setup lang="ts">
import {
  type ConsultationRequestDTO,
  ConsultationRequestStatusDTO,
  ConsultationRequestUserRelationDTO,
  MedicalTitle,
  type UrgencyDTO
} from '@/generated'
import ConsultationTimer from '@/components/consultationrequest/ConsultationTimer.vue'
import { useI18n } from 'vue-i18n'
import { computed, ref, toRef, watchEffect } from 'vue'
import DirectConsultationTypeChip from '@/atoms/DirectConsultationTypeChip.vue'
import { isUrgencyWithCountdown } from '@/types/UrgencyWithCountdown'
import { useConsultationRequest } from '@/composables/useConsultationRequest'
import ConsultationCardActions from '@/components/consultationrequest/ConsultationCardActions.vue'
import DividerLine from '@/atoms/DividerLine.vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user'
import { RoutingSupervisorService } from '@/services/telehealthApi/RoutingSupervisorService'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'
import VitalSignsPreviewModal from '@/components/patient/components/molecules/VitalSignsPreviewModal.vue'
import { VProgressLinear } from 'vuetify/components'
import ConsultationStepper from '@/components/consultationrequest/ConsultationStepper.vue'
import NoRecipientsBanner from '@/components/consultationrequest/NoRecipientsBanner.vue'
import useFormattedPatientDetails from '@/composables/useFormattedPatientDetails'
import useFormattedCaseDetails from '@/composables/useFormattedCaseDetails'
import FormattedMedicalText from '@/atoms/FormattedMedicalText.vue'
import MoreItemsLabel from '@/components/consultationrequest/MoreItemsLabel.vue'
import { convertUserDetailsIntoSimpleObjects } from '@/util/convertRequesterDetails'
import PersonAvatar from '@/components/navigation/PersonAvatar.vue'
import TimeCounter from '@/components/common/TimeCounter.vue'
import { translateMedicalFieldsDTO } from '@/util/translations'
import { useFormatDate } from '@/composables/useFormatDate'

const { t, locale } = useI18n()
const noticeStore = useNoticeStore()
const { formatDateShort, formatTime } = useFormatDate()

const { hasChiefPhysicianShift, user } = storeToRefs(useUserStore())
const isEditing = ref(false)
const availableDoctors = ref<{ text: string; value: string }[]>([])
const selectedDoctor = ref<string>('')
const isLoadingDoctors = ref(false)
const isSavingDoctor = ref(false)
const showVitalSignsPreviewModal = ref(false)
const search = ref('')
const filteredDoctors = ref<{ text: string; value: string }[]>([])
const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const { caseWithVitalSigns } = storeToRefs(patientsWithVitalSignsStore)

const emit = defineEmits<{
  (e: 'updateAssignedDoctor', value: ConsultationRequestDTO['assignedTo']): void
}>()

const activeCase = caseWithVitalSigns
const caseLoading = ref(false)
const consultationUrgencyWithDetails: UrgencyDTO[] = ['routine', 'urgent']
const showDetails = ref(false)

const canSave = computed(
  () => selectedDoctor.value && selectedDoctor.value !== consultation.assignedTo?.userName
)

watchEffect(() => {
  const q = (search.value || '').toLowerCase()
  filteredDoctors.value = availableDoctors.value.filter((doc) => doc.text.toLowerCase().includes(q))
})

const {
  consultation,
  showActionButtons = true,
  showEditButton = true
} = defineProps<{
  consultation: ConsultationRequestDTO
  showActionButtons?: boolean
  showEditButton?: boolean
}>()

const reactiveConsultation = toRef(() => consultation)

const showNoRecipientsWarning = computed(
  () =>
    consultation.relationToUser === ConsultationRequestUserRelationDTO.OUTGOING &&
    !consultation.recipientsAvailable
)

const { medicalHistory, diagnosis, therapy, question } =
  useConsultationRequest(reactiveConsultation)
const { getFormattedAge, getFormattedPatientName } = useFormattedPatientDetails()
const { getFormattedCaseId } = useFormattedCaseDetails()

const fetchAvailableDoctors = async (consultationRequestId: string) => {
  isLoadingDoctors.value = true
  try {
    const response =
      await RoutingSupervisorService.getDoctorsAvailableForConsultationRequestAssignment(
        consultationRequestId
      )

    availableDoctors.value = response.users
      .map((user) => ({
        text: [user.title, user.firstName, user.lastName].filter(Boolean).join(' ').trim(),
        value: user.userName
      }))
      .sort((a, b) => a.text.localeCompare(b.text))
  } catch (error) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to load doctors')
    })
  } finally {
    isLoadingDoctors.value = false
  }
}

const consultationDetails = computed(() => ({
  age: getFormattedAge(consultation.case.patient),
  patientName: getFormattedPatientName(consultation.case.patient),
  caseId: getFormattedCaseId(consultation.case.externalId)
}))

const assignDoctor = async (consultationRequestId: string) => {
  const currentAssignedUser = consultation.assignedTo?.userName

  if (selectedDoctor.value === currentAssignedUser) {
    isEditing.value = false
    return
  }

  if (isSavingDoctor.value) return

  if (!selectedDoctor.value?.trim()) return

  isSavingDoctor.value = true
  try {
    await RoutingSupervisorService.assignDoctorToConsultationRequest(consultationRequestId, {
      userName: selectedDoctor.value
    })
    const assignedDoctor = availableDoctors.value.find((doc) => doc.value === selectedDoctor.value)
    if (assignedDoctor) {
      const [title, firstName, ...rest] = assignedDoctor.text.split(' ')
      const lastName = rest.join(' ')
      emit('updateAssignedDoctor', {
        title: title as MedicalTitle,
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        userName: selectedDoctor.value
      })
    }
    isEditing.value = false
    noticeStore.add({
      type: NoticeType.SUCCESS,
      message: t('Doctor successfully assigned')
    })
  } catch (error) {
    noticeStore.add({
      type: NoticeType.ERROR,
      message: t('Failed to assign doctor. Please try again.')
    })
  } finally {
    isSavingDoctor.value = false
  }
}

const startEdit = async () => {
  isEditing.value = true
  const assignedTo = consultation.assignedTo
  const assignedUser = assignedTo?.userName

  await fetchAvailableDoctors(consultation.id)

  if (assignedUser) {
    const exists = availableDoctors.value.some((doc) => doc.value === assignedUser)
    if (!exists) {
      const assignedDisplayName =
        [assignedTo.title, assignedTo.firstName, assignedTo.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() ?? assignedUser

      availableDoctors.value.push({
        text: assignedDisplayName,
        value: assignedUser
      })

      availableDoctors.value = Array.from(
        new Map(availableDoctors.value.map((doc) => [doc.value, doc])).values()
      )

      availableDoctors.value.sort((a, b) => a.text.localeCompare(b.text))
    }
  }

  if (assignedUser) {
    selectedDoctor.value = assignedUser
  }
}

async function openVitalSignsModal() {
  if (caseLoading.value || isEditing.value) return

  showVitalSignsPreviewModal.value = false
  caseLoading.value = true

  await patientsWithVitalSignsStore.getCaseWithVitalSigns(
    consultation.case.id,
    consultation.case.ward.id
  )

  if (caseWithVitalSigns.value) {
    showVitalSignsPreviewModal.value = true
  }

  caseLoading.value = false
}

const cancelEdit = () => {
  isEditing.value = false
}
</script>

<style scoped lang="scss">
.consultation-details-expansion-panel {
  background-color: transparent;

  :deep(.v-expansion-panel-title) {
    padding-block: 8px;
    flex-direction: row-reverse;
    padding-inline: 0;
    justify-content: flex-end;
    min-height: 0 !important;

    .v-expansion-panel-title__icon {
      margin: 0;
      color: #2f7494;
    }
  }
}

:deep(.v-expansion-panel--active) {
  .v-icon {
    transform: rotate(180deg);
  }
}
</style>
